import type { RevenueCommissionEntry, RevenuePayoutItem, RevenuePayoutRecord } from "../../types/revenueOps";

export const PAYOUT_PAYMENT_METHODS = ["manual_ach", "check", "wire", "stripe_outside_toren", "other"] as const;
export type PayoutPaymentMethod = (typeof PAYOUT_PAYMENT_METHODS)[number];

export type PayoutPaymentConfirmation = {
  paid_at: string;
  payment_method: PayoutPaymentMethod;
  payment_reference: string;
  payment_notes?: string;
};

export function validatePayoutPaymentConfirmation(batch: RevenuePayoutRecord, items: RevenuePayoutItem[], entries: RevenueCommissionEntry[], confirmation: Partial<PayoutPaymentConfirmation>): string | null {
  if (batch.status === "canceled") return "Canceled payout batches cannot be marked paid.";
  if (batch.status === "paid") return "This payout batch is already marked paid.";
  if (!items.length) return "A payout batch must contain at least one item before payment can be recorded.";
  if (Math.abs(items.reduce((sum, item) => sum + item.amount, 0) - batch.amount) > 0.005) return "Payout batch total does not match its item total.";
  if (items.some((item) => !item.commission_entry_id)) return "Every payout item must have a commission entry before payment can be recorded.";
  const entriesById = new Map(entries.map((entry) => [entry.id, entry]));
  if (items.some((item) => {
    const entry = entriesById.get(item.commission_entry_id);
    return !entry || entry.status === "manual_hold" || entry.status === "void" || entry.status === "paid";
  })) return "Held, void, paid, or missing commission entries cannot be marked paid.";
  if (!confirmation.paid_at) return "Paid date is required.";
  if (!confirmation.payment_method || !PAYOUT_PAYMENT_METHODS.includes(confirmation.payment_method as PayoutPaymentMethod)) return "Select a payment method.";
  if (!confirmation.payment_reference?.trim() && !confirmation.payment_notes?.trim()) return "Add a payment reference or payment notes.";
  return null;
}

export function buildPayoutPaymentNotes(confirmation: PayoutPaymentConfirmation): string {
  const reference = confirmation.payment_reference.trim();
  const notes = confirmation.payment_notes?.trim();
  return [
    "External payment recorded outside Toren.",
    `Method: ${confirmation.payment_method}`,
    reference ? `Reference: ${reference}` : "Reference: not provided",
    notes ? `Notes: ${notes}` : "",
  ].filter(Boolean).join(" ");
}

export function buildPayoutPaymentAuditMetadata(batch: RevenuePayoutRecord, items: RevenuePayoutItem[], confirmation: PayoutPaymentConfirmation) {
  return {
    batch_id: batch.id,
    paid_at: confirmation.paid_at,
    payment_method: confirmation.payment_method,
    payment_reference_present: Boolean(confirmation.payment_reference.trim()),
    total_amount: batch.amount,
    item_count: items.length,
    commission_entry_ids: items.map((item) => item.commission_entry_id),
  };
}
